import { get } from 'lodash';
import { DayOfWeek } from '../../models/day-enum';
import { ILocationsState, ISearchLocations, LOCATIONS_STATE_KEY } from './offices-reducer';
import { ManagersEnum } from '../collapsible-panel/managers.enum';
import { PsPartnersEnum } from '../collapsible-panel/ps-partners.enum';
import { HrPartnersEnum } from '../collapsible-panel/hr-partners.enum';
import moment from 'moment';
import { mapSchedulesToForm } from '../../common/helpers/mappers';
import { ScheduleDto } from '../../models/schedule-dto';
import { VENDORS_STATE_KEY } from '../vendors/vendors-reducer';

export const getStub = () => [];

export const getIsLoading = (state: any) => state[LOCATIONS_STATE_KEY].loading;
export const getIsLoadingLocationNumbers = (state: any) => state[LOCATIONS_STATE_KEY].loadingLocationNumbers;
export const getError = (state: any) => state[LOCATIONS_STATE_KEY].error;
export const getLocations = (state: any) => state[LOCATIONS_STATE_KEY].locations;
export const getLastLocationSave = (state: any) => state[LOCATIONS_STATE_KEY].lastLocationSave;
export const getLocationById = (state: any, id: string) =>
  state[LOCATIONS_STATE_KEY].locations.contents.find((loc) => loc.id === id);
export const getRegions = (state: any) => state[LOCATIONS_STATE_KEY].regions;
export const getDistricts = (state: any) => state[LOCATIONS_STATE_KEY].districts;
export const getContacts = (state: any) => state[LOCATIONS_STATE_KEY].contacts;
export const getSearch = (state: ILocationsState): ISearchLocations => state[LOCATIONS_STATE_KEY].search;
export const getSearchRegionId = (state: any) => state[LOCATIONS_STATE_KEY].searchRegionId;
export const getAssignmentCount = (state: any) => state[LOCATIONS_STATE_KEY].assignmentCount;
export const getSelectedStaff = (state: any) => state[LOCATIONS_STATE_KEY].selectedStaff;
export const getSelectedLocations = (state: any) => state[LOCATIONS_STATE_KEY].selectedLocations;
export const getOfficeList = (state: any) => state[LOCATIONS_STATE_KEY].officeList;
export const getOfficeListLoading = (state: any) => state[LOCATIONS_STATE_KEY].officeListLoading;
export const getOfficeListHasMore = (state: any) => state[LOCATIONS_STATE_KEY].officeListHasMore;
export const getLocationsTotal = (state: any) => getLocations(state).total;
export const getLocationsForSearch = (state: any) => state[LOCATIONS_STATE_KEY].locationsForSearch;
export const getProServices = (state: any) => state[LOCATIONS_STATE_KEY].proServices;
export const getSpecialties = (state: any) => state[LOCATIONS_STATE_KEY].specialties;
export const getVendors = (state: any) => state[LOCATIONS_STATE_KEY].vendors;
export const getCloseDrawer = (state: any) => state[LOCATIONS_STATE_KEY].closeDrawer;
export const getLocationsForGrid = (state: any) =>
  getLocations(state).contents.map((content: any) => {
    let addressResult = '';
    const address = get(content, 'address', null);

    if (address) {
      const { id, lat, lng, phone, fax, ...addressFields } = get(content, 'address', {});
      addressResult = [
        ...Object.values(addressFields).filter((field) => !!field),
        ...(phone ? [`P:${phone}`] : []),
        ...(fax ? [`F:${fax}`] : []),
      ].join(', ');
    }

    return {
      key: content.id,
      locationName: content.locationName,
      locationNumber: content.locationNumber,
      region: get(content, 'region.name', null),
      regionId: get(content, 'region.id', null),
      district: get(content, 'district.name', null),
      districtId: get(content, 'district.id', null),
      county: content.county,
      address: addressResult,
      status: content.status,
      directDial: content.directDial,
      timezone: content.timezone,
      ctc: content.ctc,
      openingDate: content.openingDate,
    };
  });
export const getProfileForGrid = (state: any) =>
  getLocations(state).contents.map((content: any) => ({
    key: content.id,
    locationName: content.locationName,
    locationNumber: content.locationNumber,
    openingDate: content.openingDate,
    officeQuickCode: content.officeQuickCode,
    email: content.email,
    odOnCall: content.odOnCall,
    squareFootage: content.squareFootage,
    leaseExpiration: content.leaseExpiration,
    unified: content.unified,
    labOnsite: content.labOnsite,
    status: content.status,
    modifiedDate: moment(content.modifiedDate).format('YYYY-MM-DD HH:mm:ss'),
    externalLocationName: content.externalLocationName,
    locatedIn: content.locatedIn,
    pos: content.pos,
    encompassLaunchDate: content.encompassLaunchDate,
  }));
export const gerProfileForLocation = (state: any, id: string) => ({
  ...getProfileForGrid(state).find((profile) => profile.key === id),
});
export const getFinanceForGrid = (state: any) =>
  getLocations(state).contents.map((content: any) => {
    return {
      key: content.id,
      locationName: content.locationName,
      locationNumber: content.locationNumber,
      legalName: content.legalName,
      taxid: content.taxid,
      tillAmount: (get(content, 'tillAmount') ?? 0).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
      drOfRecord: get(content, 'billing.drOfRecord', null),
      oracleLegalEntityNum: get(content, 'billing.oracleLegalEntityNum', null),
      oracleOfficeNum: get(content, 'billing.oracleOfficeNum', null),
      depositoryBankAccount: get(content, 'billing.depositoryBankAccount', null),
      worldpayMerchantId: get(content, 'billing.worldpayMerchantId', null),
      worldpayChainCode: get(content, 'billing.worldpayChainCode', null),
      careCreditMerchantId: get(content, 'billing.careCreditMerchantId', null),
      worldpaySerialNumber: get(content, 'billing.worldpaySerialNumber', null),
      vintageName: content.vintageName,
      mergedInto: get(content, 'mergedInto.locationNumber', null),
      closedDate: content.closedDate,
      status: content.status,
      depositMethod: content.depositMethod,
      locationCategory: content.locationCategory,
      sameOfficeSales: content?.sameOfficeSales,
      sameOfficeChangeDate: content?.sameOfficeChangeDate,
      accountingIntegrationDate: content?.accountingIntegrationDate,
      acuityLogicLaunchDate: content.acuityLogicLaunchDate,
      newOfficeYear: content.newOfficeYear,
      acquisitionYear: content.acquisitionYear,
      jointVentureYear: content.jointVentureYear,
      preAcquisitionAnnualRevenue: content.preAcquisitionAnnualRevenue,
    };
  });
export const getBillingInfoForLocation = (state: any, id: string) => {
  let result = {};
  const loc = getLocationById(state, id);
  if (loc) {
    result = {
      legalName: loc.legalName,
      taxid: loc.taxid,
      closedDate: loc.closedDate,
      vintageName: loc.vintageName,
      mergedInto: { id: loc.mergedInto?.id, name: loc.mergedInto?.locationNumber },
      depositMethod: loc.depositMethod,
    };
  }
  return result;
};
export const getAddressForLocation = (state: any, id: string): any => {
  let result = {};
  const loc = getLocationById(state, id);

  if (loc) {
    result = {
      ...get(loc, 'address', {
        city: null,
        fax: null,
        id: null,
        phone: null,
        state: null,
        street: null,
        street2: null,
        zip: null,
      }),
      pos: get(loc, 'pos', null),
      locationCategory: get(loc, 'locationCategory', null),
      encompassLaunchDate: get(loc, 'encompassLaunchDate', null),
    };
  }
  return result;
};
export const getBillingAccount = (state: any, id: string) => {
  let result = {};
  const loc = getLocationById(state, id);

  if (loc) {
    result = {
      id: get(loc, 'billing.id', null),
      tillAmount: (get(loc, 'tillAmount') ?? 0)
        .toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
        })
        .replace('$', ''),
      drOfRecord: get(loc, 'billing.drOfRecord', null),
      oracleLegalEntityNum: get(loc, 'billing.oracleLegalEntityNum', null),
      oracleOfficeNum: get(loc, 'billing.oracleOfficeNum', null),
      depositoryBankAccount: get(loc, 'billing.depositoryBankAccount', null),
      worldpayMerchantId: get(loc, 'billing.worldpayMerchantId', null),
      worldpayChainCode: get(loc, 'billing.worldpayChainCode', null),
      careCreditMerchantId: get(loc, 'billing.careCreditMerchantId', null),
      worldpaySerialNumber: get(loc, 'billing.worldpaySerialNumber', null),
      integration: get(loc, 'integration', null),
      sameOfficeSales: get(loc, 'sameOfficeSales', null),
      sameOfficeChangeDate: get(loc, 'sameOfficeChangeDate', null),
      accountingIntegrationDate: get(loc, 'accountingIntegrationDate', null),
      acuityLogicLaunchDate: get(loc, 'acuityLogicLaunchDate', null),
      pos: get(loc, 'pos', null),
      locationCategory: get(loc, 'locationCategory', null),
      encompassLaunchDate: get(loc, 'encompassLaunchDate', null),
      newOfficeYear: get(loc, 'newOfficeYear', null),
      acquisitionYear: get(loc, 'acquisitionYear', null),
      jointVentureYear: get(loc, 'jointVentureYear', null),
      preAcquisitionAnnualRevenue: get(loc, 'preAcquisitionAnnualRevenue', null),
      vintageName: get(loc, 'vintageName', null),
    };
  }
  return result;
};
export const getMarketingForGrid = (state: any) =>
  getLocations(state).contents.map((content: any) => ({
    key: content.id,
    locationName: content.locationName,
    locationNumber: content.locationNumber,
    branding: content.branding,
    brandingType: content.brandingType,
    zocDocPatientType: content.zocDocPatientType,
    legacyName: content.legacyName,
    mediaMarket: content.mediaMarket,
    msa: content.msa,
    top5zip: content.top5zip,
    digitalMetroCategory: content.digitalMetroCategory,
    status: content.status,
    storefrontCategory: content.storefrontCategory,
    otherSfcComment: content.otherSfcComment,
    googleBookOnlineButtonType: content.googleBookOnlineButtonType,
  }));

export const getMarketingForLocation = (state: any, id: string) => ({
  ...getMarketingForGrid(state).find((marketing) => marketing.key === id),
});
export const getScheduleForGrid = (state: any, includeTemporaryClosures = true) =>
  getLocations(state).contents.map((content: any) => {
    const locationSchedules: ScheduleDto[] = get(content, 'schedules', []);
    const coreSchedules: ScheduleDto[] = get(content, 'coreSchedules', []);
    const schedules: ScheduleDto[] =
      includeTemporaryClosures || coreSchedules.length === 0 ? locationSchedules : coreSchedules;
    const temporarySchedules: ScheduleDto[] = includeTemporaryClosures ? get(content, 'temporarySchedules', []) : [];
    const selectedDate = moment(getSearch(state).effectiveDate || undefined);
    const coreWeekStart = selectedDate.clone().startOf('day').day(0);
    const currentDaySchedule = includeTemporaryClosures
      ? schedules.find((schedule) => schedule.dayOfWeek?.toLowerCase() === selectedDate.format('dddd').toLowerCase())
      : undefined;
    const effectiveDate =
      currentDaySchedule?.validFrom ||
      schedules.reduce((result, day) => {
        return !result
          ? day.validFrom
          : includeTemporaryClosures
          ? moment(day.validFrom).isBefore(result)
            ? day.validFrom
            : result
          : moment(day.validFrom).isAfter(result)
          ? day.validFrom
          : result;
      }, '');

    const isTemporaryWindow = schedules.length > 0 && dayDiff(schedules[0].validFrom, schedules[0].validTo) === 6;

    const allDays = Object.keys(DayOfWeek).filter((d) => isNaN(Number(d)));
    const normalizeDate = (value: string) => {
      const dateString = String(value || '');
      const dateOnly = dateString.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
      return dateOnly || (value ? moment(value).format('YYYY-MM-DD') : '');
    };

    return allDays.reduce(
      (acc: any, dayKey: string) => {
        const day = DayOfWeek[dayKey as keyof typeof DayOfWeek];

        const matchingDaySchedules = schedules.filter(
          (schedule) => schedule.dayOfWeek?.toLowerCase() === dayKey.toLowerCase()
        );
        const dayDate = coreWeekStart.clone().add(allDays.indexOf(dayKey), 'days').format('YYYY-MM-DD');
        const item = includeTemporaryClosures
          ? matchingDaySchedules[0]
          : matchingDaySchedules
              .filter((schedule) => {
                const validFrom = normalizeDate(schedule.validFrom);
                const validTo = normalizeDate(schedule.validTo);
                return validFrom && validFrom <= dayDate && (!validTo || validTo >= dayDate);
              })
              .sort((first, second) =>
                normalizeDate(second.validFrom).localeCompare(normalizeDate(first.validFrom))
              )[0];
        // Check if day is in temporarySchedules
        const isTemp = temporarySchedules.some((ts) => ts.dayOfWeek?.toLowerCase() === dayKey.toLowerCase());

        if (item) {
          const isClosed = !item.open || !item.close || item.open === '00:00' || item.close === '00:00';

          acc[day] = {
            open: item.open,
            close: item.close,
            value: item.open && item.close ? `${item.open}-${item.close}` : null,
            validFrom: item.validFrom,
            validTo: item.validTo,
            dayOfWeek: item.dayOfWeek,
            isClosed,
            isTemporaryClosed: item.isTemporaryClosed === true,
          };
        } else {
          acc[day] = {
            open: null,
            close: null,
            value: null,
            validFrom: schedules[0]?.validFrom,
            validTo: schedules[0]?.validTo,
            dayOfWeek: dayKey,
            isClosed: true,
            isTemporaryClosed: isTemp,
          };
        }

        return acc;
      },
      {
        key: content.id,
        locationName: content.locationName,
        locationNumber: content.locationNumber,
        status: content.status,
        timezone: content.timezone,
        effectiveDate,
      }
    );
  });

const dayDiff = (from: string, to: string) => {
  const start = new Date(from);
  const end = new Date(to);

  return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
};

export const getLocationVendorData = (state: any) => state[LOCATIONS_STATE_KEY].locationVendorData;

export const getMvcForGrid = (state: any) => {
  const vendorsList = state[VENDORS_STATE_KEY]?.vendorDetails || [];
  const locationVendorData = getLocationVendorData(state) || [];

  return getLocations(state).contents.map((content: any) => {
    // Get vendorAccounts from locationVendorData (fetched with activeTab='vendor')
    const locationVendor = locationVendorData.find((loc: any) => loc.id === content.id);
    const vendorAccounts = locationVendor?.vendorAccounts || [];

    // Get safety vendor names by cross-referencing vendorAccounts with vendors list
    const safetyVendorNames =
      vendorAccounts
        ?.filter((va: any) => {
          const vendor = vendorsList.find((v: any) => v.id === va.vendorId);
          return vendor?.safetyVendor === true && va.active;
        })
        ?.map((va: any) => {
          const accountNum = va.accountNumber && va.accountNumber !== 'null' ? va.accountNumber : null;
          return accountNum ? `${va.vendorName}-${accountNum}` : va.vendorName;
        })
        ?.join(', ') || null;

    return {
      key: content.id,
      locationName: content.locationName,
      locationNumber: content.locationNumber,
      medicalRecordsType: content.medicalRecordsType,
      acuityLogicLaunchDate: content.acuityLogicLaunchDate,
      cliaExpiration: content.cliaExpiration,
      professionalServicesAnalyst: get(
        content.staff?.find((item) => item.job === 'PSA'),
        'name',
        null
      ),
      hrBusinessPartner: get(
        content.staff?.find((item) => item.job === 'HRBP'),
        'name',
        null
      ),
      medicaidLocation: content.medicaidLocation,
      davisLocation: content.davisLocation,
      // vspLocation: content.vspLocation,
      status: content.status,
      groupNpi: content.groupNpi,
      epId: content.epId,
      bcbs: content.bcbs,
      medicare: content.medicare,
      tricare: content.tricare,
      legacyArAcq: content.legacyArAcq,
      legacyArClosed: content.legacyArClosed,
      transitionArClosed: content.transitionArClosed,
      naLocation: content.naLocation,
      vspStatus: content.vspStatus,
      vintageName: content.vintageName,
      medicareDme: content.medicareDme,
      davisVision: content.davisVision,
      superiorVision: content.superiorVision,
      spectera: content.spectera,
      safetyVendors: safetyVendorNames,
    };
  });
};
export const getMvcForLocation = (state: any, id: string) => ({
  ...getMvcForGrid(state).find((mvc) => mvc.key === id),
});
export const getBillingForGrid = (state: any) =>
  getLocations(state).contents.map((content: any) => {
    let billingAddress = '';
    const address = get(content, 'address', null);

    if (address) {
      const { id, ...addressFields } = get(content, 'address', {});
      billingAddress = Object.values(addressFields).join(', ');
    }
    return {
      key: content.id,
      merchantBankRouting: get(content, 'billing.merchantBankRouting', null),
      tillAmount: get(content, 'tillAmount', null),
      drOfRecord: get(content, 'billing.drOfRecord', null),
      oracleLegalEntityNum: get(content, 'billing.oracleLegalEntityNum', null),
      oracleOfficeNum: get(content, 'billing.oracleOfficeNum', null),
      depositoryBankAccount: get(content, 'billing.depositoryBankAccount', null),
      worldpayMerchantId: get(content, 'billing.worldpayMerchantId', null),
      worldpayChainCode: get(content, 'billing.worldpayChainCode', null),
      careCreditMerchantId: get(content, 'billing.careCreditMerchantId', null),
      worldpaySerialNumber: get(content, 'billing.worldpaySerialNumber', null),
      action: get(content, 'billing.action', null),
    };
  });

export const getManagersForLocation = (state: any, id: string) => {
  let result = [];
  const loc = getLocationById(state, id);

  if (loc) {
    const data = loc.staff.filter((item) => Object.keys(ManagersEnum).includes(item['job']));
    const enumOrder = Object.keys(ManagersEnum);
    result = data.sort((a: any, b: any) => {
      return enumOrder.indexOf(a.job) - enumOrder.indexOf(b.job);
    });
  }

  return result;
};

export const getDoctorsForLocation = (state: any, id: string) => {
  let result = [];
  const loc = getLocationById(state, id);

  if (loc) {
    result = loc.staff.filter((doc) => doc.job === 'OD');
  }

  return result;
};

export const getPsPartnersForLocation = (state: any, id: string) => {
  let result = [];
  const loc = getLocationById(state, id);

  if (loc) {
    const data = loc.staff.filter((item) => Object.keys(PsPartnersEnum).includes(item['job']));
    const enumOrder = Object.keys(PsPartnersEnum);
    result = data.sort((a: any, b: any) => {
      return enumOrder.indexOf(a.job) - enumOrder.indexOf(b.job);
    });
  }

  return result;
};

export const getHrPartnersForLocation = (state: any, id: string) => {
  let result = [];
  const loc = getLocationById(state, id);

  if (loc) {
    const data = loc.staff.filter((item) => Object.keys(HrPartnersEnum).includes(item['job']));
    const enumOrder = Object.keys(HrPartnersEnum);
    result = data.sort((a: any, b: any) => {
      return enumOrder.indexOf(a.job) - enumOrder.indexOf(b.job);
    });
  }

  return result;
};

export interface ILocationDetails {
  district?: string;
  region?: string;
  county?: string;
  status?: string;
  // address
  id?: number;
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  fax?: string;
}

export const getLocationDetails = (state: any, id: string): ILocationDetails | null => {
  const loc = getLocationById(state, id);
  let result: ILocationDetails | null = null;
  if (loc) {
    result = {
      ...get(loc, 'address', null),
      district: get(loc, 'district.id', null),
      region: get(loc, 'region.id', null),
      county: get(loc, 'county', null),
      status: get(loc, 'status', null),
      timezone: get(loc, 'timezone', null),
      directDial: get(loc, 'directDial', null),
      locationName: loc.locationName,
      openingDate: get(loc, 'openingDate', null),
      pos: get(loc, 'pos', null),
      locationCategory: get(loc, 'locationCategory', null),
      encompassLaunchDate: get(loc, 'encompassLaunchDate', null),
      ctc: get(loc, 'ctc', null),
    };
  }
  return result;
};

export const getSchedulesForLocation = (state: any, id: string, isCoreSchedule = false) => {
  const location = getLocationById(state, id);
  const coreSchedules = get(location, 'coreSchedules', []);
  const schedules = isCoreSchedule && coreSchedules.length > 0 ? coreSchedules : location.schedules;
  return mapSchedulesToForm(schedules);
};

export const getTimezoneForLocation = (state: any, id: string) => getLocationById(state, id).timezone;

export const getProServicesForGrid = (state: any) =>
  getLocations(state).contents.map((content: any) => {
    return {
      key: content.id,
      locationName: content.locationName,
      locationNumber: content.locationNumber,
      odOnCall: content.odOnCall,
      onCallPhone: content.onCallPhone,
      status: content.status,
      preceptorLocation: content.preceptorLocation,
      dryEyeProgram: content.dryEyeProgram,
      locationEquipments: content.locationEquipments?.map(({ id, fullName }) => id),
    };
  });

export const getProServiceForLocation = (state: any, id: string) => ({
  ...getProServicesForGrid(state).find((proService) => proService.key === id),
});

export const getItServicesForGrid = (state: any) =>
  getLocations(state).contents.map((content: any) => {
    return {
      key: content.id,
      locationName: content.locationName,
      locationNumber: content.locationNumber,
      status: content?.status,

      mspAccountNumber: get(content, 'locationInfra.mspAccountNumber', null),
      vendor: get(content, 'locationInfra.vendor', null),
      networkStandardized: get(content, 'locationInfra.networkStandardized', null) ? 'Yes' : 'No',
      tier: get(content, 'locationInfra.tier', null),
      provider: get(content, 'locationInfra.provider', null),
      speed: get(content, 'locationInfra.speed', null),
      circuitType: get(content, 'locationInfra.circuitType', null),
      mspStatus: get(content, 'locationInfra.mspStatus', null),
      completionDate: get(content, 'locationInfra.completionDate', null),
      notes: get(content, 'locationInfra.notes', null),
      providerSecondary: get(content, 'locationInfra.providerSecondary', null),
      speedSecondary: get(content, 'locationInfra.speedSecondary', null),
      typeSecondary: get(content, 'locationInfra.typeSecondary', null),

      routerModel: get(content, 'locationInfra.routerModel', null),
      routerModelCount: get(content, 'locationInfra.routerModelCount', null),
      switches: get(content, 'locationInfra.switches', null),
      waps: get(content, 'locationInfra.waps', null),
      vvxPhones: get(content, 'locationInfra.vvxPhones', null),
      cordlessPhones: get(content, 'locationInfra.cordlessPhones', null),

      lanSubnet: get(content, 'locationInfra.lanSubnet', null),
      gatewayDHCP: get(content, 'locationInfra.gatewayDHCP', null),
      range: get(content, 'locationInfra.range', null),

      staticIP: get(content, 'locationInfra.staticIP', null),
      gateway: get(content, 'locationInfra.gateway', null),
      dnsPrimary: get(content, 'locationInfra.dnsPrimary', null),
      dnsSecondary: get(content, 'locationInfra.dnsSecondary', null),
      subnetMask: get(content, 'locationInfra.subnetMask', null),
    };
  });

export const getItDetailsForLocation = (state: any, id: string): any => {
  let result = {};
  const content = getLocationById(state, id);

  if (content) {
    result = {
      key: content.id,
      id: get(content, 'locationInfra.id', null),
      locationId: content.id,

      mspAccountNumber: get(content, 'locationInfra.mspAccountNumber', null),
      vendor: get(content, 'locationInfra.vendor', null),
      networkStandardized: get(content, 'locationInfra.networkStandardized', null),
      tier: get(content, 'locationInfra.tier', null),
    };
  }

  return result;
};

export const getItAccessDetailsForLocation = (state: any, id: string): any => {
  let result = {};
  const content = getLocationById(state, id);

  if (content) {
    result = {
      key: content.id,

      provider: get(content, 'locationInfra.provider', null),
      speed: get(content, 'locationInfra.speed', null),
      circuitType: get(content, 'locationInfra.circuitType', null),
      mspStatus: get(content, 'locationInfra.mspStatus', null),
      completionDate: get(content, 'locationInfra.completionDate', null),
      notes: get(content, 'locationInfra.notes', null),
      providerSecondary: get(content, 'locationInfra.providerSecondary', null),
      speedSecondary: get(content, 'locationInfra.speedSecondary', null),
      typeSecondary: get(content, 'locationInfra.typeSecondary', null),
    };
  }

  return result;
};

export const getItEquipmentDetailsForLocation = (state: any, id: string): any => {
  let result = {};
  const content = getLocationById(state, id);

  if (content) {
    result = {
      key: content.id,
      routerModel: get(content, 'locationInfra.routerModel', null),
      routerModelCount: get(content, 'locationInfra.routerModelCount', null),
      switches: get(content, 'locationInfra.switches', null),
      waps: get(content, 'locationInfra.waps', null),
      vvxPhones: get(content, 'locationInfra.vvxPhones', null),
      cordlessPhones: get(content, 'locationInfra.cordlessPhones', null),
    };
  }

  return result;
};

export const getItLanIPDetailsForLocation = (state: any, id: string): any => {
  let result = {};
  const content = getLocationById(state, id);

  if (content) {
    result = {
      key: content.id,
      lanSubnet: get(content, 'locationInfra.lanSubnet', null),
      gatewayDHCP: get(content, 'locationInfra.gatewayDHCP', null),
      range: get(content, 'locationInfra.range', null),
    };
  }

  return result;
};
export const getItPrimaryCircuitDetailsForLocation = (state: any, id: string): any => {
  let result = {};
  const content = getLocationById(state, id);

  if (content) {
    result = {
      key: content.id,
      staticIP: get(content, 'locationInfra.staticIP', null),
      gateway: get(content, 'locationInfra.gateway', null),
      dnsPrimary: get(content, 'locationInfra.dnsPrimary', null),
      dnsSecondary: get(content, 'locationInfra.dnsSecondary', null),
      subnetMask: get(content, 'locationInfra.subnetMask', null),
    };
  }

  return result;
};

export const getVehForLocation = (state: any, id: string): any => {
  let result = {};
  const content = getLocationById(state, id);

  if (content) {
    result = {
      key: content.id,
      vehId: get(content, 'locationVeh.id', null),
      vehStatus: get(content, 'locationVeh.vehStatus', null),
      coverageModel: get(content, 'locationVeh.coverageModel', null),
      phoropterEquipment: get(content, 'locationVeh.phoropterEquipment', null),
      numberOfEquippedPretest: get(content, 'locationVeh.numberOfEquippedPretest', null),
      numberOfEquippedLanes: get(content, 'locationVeh.numberOfEquippedLanes', null),
      vehGoLive: get(content, 'locationVeh.vehGoLive', null),
      vehLastDay: get(content, 'locationVeh.vehLastDay', null),
      callCenter: get(content, 'locationVeh.callCenter', null),
      teleOptometry: get(content, 'teleOptometry', null),
      vehSystem: get(content, 'locationVeh.vehSystem', null),
      doctorNetwork: get(content, 'locationVeh.doctorNetwork', null),
      slitLampEquipment: get(content, 'locationVeh.slitLampEquipment', null),
      slitLampEquipmentOther: get(content, 'locationVeh.slitLampEquipmentOther', null),
      preTestEquipment: get(content, 'locationVeh.preTestEquipment', null),
      preTestEquipmentOther: get(content, 'locationVeh.preTestEquipmentOther', null),
    };
  }

  return result;
};

export const getVehForGrid = (state: any) =>
  getLocations(state).contents.map((content: any) => {
    return {
      key: content.id,
      locationName: content.locationName,
      locationNumber: content.locationNumber,
      status: content?.status,

      vehStatus: get(content, 'locationVeh.vehStatus', null),
      coverageModel: get(content, 'locationVeh.coverageModel', null),
      phoropterEquipment: get(content, 'locationVeh.phoropterEquipment', null),
      numberOfEquippedPretest: get(content, 'locationVeh.numberOfEquippedPretest', null),
      numberOfEquippedLanes: get(content, 'locationVeh.numberOfEquippedLanes', null),
      vehGoLive: get(content, 'locationVeh.vehGoLive', null),
      vehLastDay: get(content, 'locationVeh.vehLastDay', null),
      callCenter: get(content, 'locationVeh.callCenter', null),
      teleOptometry: get(content, 'teleOptometry', null),
      vehSystem: get(content, 'locationVeh.vehSystem', null),
      doctorNetwork: get(content, 'locationVeh.doctorNetwork', null),
      slitLampEquipment: get(content, 'locationVeh.slitLampEquipment', null),
      slitLampEquipmentOther: get(content, 'locationVeh.slitLampEquipmentOther', null),
      preTestEquipment: get(content, 'locationVeh.preTestEquipment', null),
      preTestEquipmentOther: get(content, 'locationVeh.preTestEquipmentOther', null),
    };
  });

export const getMerchandisingForLocation = (state: any, id: string): any => {
  let result = {};
  const content = getLocationById(state, id);

  if (content) {
    result = {
      key: content.id,
      merchandisingId: get(content, 'fixture.id', null),
      merchandisingTier: get(content, 'fixture.merchandisingTier', null),
      totalFrameCapacity: get(content, 'fixture.totalFrameCapacity', null),
      brandedFixtureCapacity: get(content, 'fixture.brandedFixtureCapacity', null),
      genericFixtureCapacity: get(content, 'fixture.genericFixtureCapacity', null),
      frameCapacityGroup: get(content, 'fixture.frameCapacityGroup', null),
    };
  }

  return result;
};

export const getMerchandisingForGrid = (state: any) =>
  getLocations(state).contents.map((content: any) => {
    return {
      key: content.id,
      locationName: content.locationName,
      locationNumber: content.locationNumber,
      merchandisingTier: get(content, 'fixture.merchandisingTier', null),
      totalFrameCapacity: get(content, 'fixture.totalFrameCapacity', null),
      brandedFixtureCapacity: get(content, 'fixture.brandedFixtureCapacity', null),
      genericFixtureCapacity: get(content, 'fixture.genericFixtureCapacity', null),
      frameCapacityGroup: get(content, 'fixture.frameCapacityGroup', null),
    };
  });

export const getTelecomForGrid = (state: any) =>
  getLocations(state).contents.map((content: any) => {
    return {
      key: content.id,
      locationName: content.locationName,
      locationNumber: content.locationNumber,
      officeQuickCode: content.officeQuickCode,
      pagesURL: content.pagesURL,
      commCenter: content.commCenter,
      directDial: content.directDial,
      helloAgain: content.helloAgain,
      forwarded: content.forwarded,
    };
  });

export const getTelecomForLocation = (state: any, id: string): any => {
  let result = {};
  const content = getLocationById(state, id);

  if (content) {
    result = {
      key: content.id,
      officeQuickCode: content.officeQuickCode,
      pagesURL: content.pagesURL,
      commCenter: content.commCenter,
      directDial: content.directDial,
      helloAgain: content.helloAgain,
    };
  }

  return result;
};

export const getSpecialtyForGrid = (state: any) =>
  getLocations(state).contents.map((content: any) => {
    return {
      key: content.id,
      locationName: content.locationName,
      locationNumber: content.locationNumber,
      specialtyContactLenses: content.specialtyContactLenses,
      myopiaControl: content.myopiaControl,
      dryEyeProgram: content.dryEyeProgram,
      visionTherapy: content.visionTherapy,
      generalMedicalEyecare: content.generalMedicalEyecare,
      iplTreatment: content.iplTreatment,
      tearCareTreatment: content.tearCareTreatment,
      lipiflowTreatment: content.lipiflowTreatment,
      iluxTreatment: content.iluxTreatment,
      lllt: content.lllt,
      rfTreatment: content.rfTreatment,
      yagCapsulotomy: content.yagCapsulotomy,
      sltLaser: content.sltLaser,
    };
  });

export const getSpecialtyForLocation = (state: any, id: string): any => {
  let result = {};
  const content = getLocationById(state, id);

  if (content) {
    result = {
      key: content.id,
      specialtyContactLenses: content.specialtyContactLenses,
      myopiaControl: content.myopiaControl,
      dryEyeProgram: content.dryEyeProgram,
      visionTherapy: content.visionTherapy,
      generalMedicalEyecare: content.generalMedicalEyecare,
      iplTreatment: content.iplTreatment,
      tearCareTreatment: content.tearCareTreatment,
      lipiflowTreatment: content.lipiflowTreatment,
      iluxTreatment: content.iluxTreatment,
      lllt: content.lllt,
      rfTreatment: content.rfTreatment,
      yagCapsulotomy: content.yagCapsulotomy,
      sltLaser: content.sltLaser,
    };
  }

  return result;
};

export const getPXPForGrid = (state: any) =>
  getLocations(state).contents.map((content: any) => {
    return {
      key: content.id,
      locationName: content.locationName,
      locationNumber: content.locationNumber,
      doNotPromote: content.doNotPromote,
    };
  });

export const getPXPForLocation = (state: any, id: string): any => {
  let result = {};
  const content = getLocationById(state, id);

  if (content) {
    result = {
      key: content.id,
      doNotPromote: content.doNotPromote,
      locationName: content.locationName,
      status: content.status,
    };
  }

  return result;
};

// HR rows are per HRTOD assignment (content.id = assignment id); offices without one come with a negative id and no contactId.
export const getHRForGrid = (state: any) =>
  getLocations(state).contents.map((content: any) => ({
    key: content.id,
    isEditable: !!content.contactId,
    locationName: content.locationName,
    locationNumber: content.locationNumber,
    employeeName: content.employeeName,
    employeeEmail: content.employeeEmail,
    doctorLocationGrouping: content.doctorLocationGrouping,
    stipend: content.stipend,
    pathways: content.pathways,
    tuitionReimbursement: content.tuitionReimbursement,
  }));

export const getHRForLocation = (state: any, id: string): any => {
  const content = getLocationById(state, id);

  return content
    ? {
        key: content.id,
        locationName: content.locationName,
        employeeName: content.employeeName,
        employeeEmail: content.employeeEmail,
        doctorLocationGrouping: content.doctorLocationGrouping,
        stipend: content.stipend,
        pathways: content.pathways,
        tuitionReimbursement: content.tuitionReimbursement,
      }
    : {};
};

export const getVendorForGrid = (state: any) =>
  getLocations(state).contents.map((content: any) => {
    const result = {
      key: content.id,
      locationName: content.locationName,
      locationNumber: content.locationNumber,
    };
    content?.vendorAccounts?.map((item: any) => {
      if (content.locationNumber === item.locationNumber) {
        result[`${item.vendorName}_acc`] = item.accountNumber;
      }
    });

    return result;
  });

export const getVendorForLocation = (state: any, id: string): any => {
  let result: any = { locationId: Number, defaultVendorFalse: [], defaultVendorTrue: [] };
  const content = getLocationById(state, id);
  result.locationId = content.id;
  content?.vendorAccounts?.map((item: any) => {
    if (item && item.isDefault === true && item?.active) {
      result?.defaultVendorTrue?.push({
        id: item.id,
        key: item.vendorId,
        name: item.vendorName,
        accountNumber: item.accountNumber !== 'null' ? item.accountNumber : null,
        isDefault: item.isDefault,
        vendorAccountId: item.vendorAccountId,
      });
    } else if (item && item.isDefault === false && item?.active) {
      result?.defaultVendorFalse?.push({
        id: item.id,
        vendorId: item.vendorId,
        name: item.vendorName,
        accountNumber: item.accountNumber,
        isDefault: item.isDefault,
        vendorAccountId: item.vendorAccountId,
      });
    }
  });
  return result;
};
